import { useState, useRef } from "react";
import { Search, X, Loader2, Building2, Truck, Network } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../ui/input";

/**
 * Jun-2026 (User-Wunsch) — Werkstatt-Verknüpfung.
 * Steuert, welche Werkstatt-Standorte für Miet-/Werkstatt-/Ersatzfahrzeuge
 * verfügbar sind. Genau ein Modus (Radio):
 *   - standalone: eigenständig, nur eigener Standort
 *   - supplied:   wird von einer Zentralwerkstatt (Händler) versorgt (+ Wochentage)
 *   - central:    ist selbst Zentralwerkstatt für ausgewählte Händler
 *
 * Props: value = { workshop_linkage_mode, workshop_central_dealer_id,
 *   workshop_central_dealer_name, workshop_supply_days, workshop_served_dealer_ids }
 *        onChange(key, val)
 */
const WEEKDAYS = [
    { k: "mon", l: "Mo" }, { k: "tue", l: "Di" }, { k: "wed", l: "Mi" },
    { k: "thu", l: "Do" }, { k: "fri", l: "Fr" }, { k: "sat", l: "Sa" }, { k: "sun", l: "So" },
];

function DealerSearch({ exclude = [], onPick, placeholder = "Händler suchen…", testid }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);

    const run = (val) => {
        setQ(val);
        if (timer.current) clearTimeout(timer.current);
        if (!val.trim()) { setResults([]); return; }
        timer.current = setTimeout(async () => {
            setBusy(true);
            try {
                const { data } = await api.get(`/dealers/directory?q=${encodeURIComponent(val.trim())}`);
                setResults((data.items || []).filter((d) => !exclude.includes(d.id)).slice(0, 8));
            } catch { setResults([]); }
            finally { setBusy(false); }
        }, 300);
    };

    return (
        <div className="relative">
            <div className="flex items-center gap-2 border border-gray-300 rounded-sm px-2 h-9 bg-white">
                <Search className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                    value={q} onChange={(e) => run(e.target.value)} placeholder={placeholder}
                    className="flex-1 text-sm outline-none bg-transparent" data-testid={testid}
                />
                {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            </div>
            {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid={`${testid}-results`}>
                    {results.map((d) => (
                        <button
                            key={d.id} type="button"
                            onClick={() => { onPick(d); setQ(""); setResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                            data-testid={`${testid}-opt-${d.id}`}
                        >
                            <span className="font-medium">{d.name}</span>
                            {d.city && <span className="text-xs text-gray-400">{d.city}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const RADIO = [
    { v: "standalone", icon: Building2, title: "Eigenständige Werkstatt", desc: "Ohne weitere Standorte — es wird nur der eigene Standort für Werkstattfahrzeuge angezeigt." },
    { v: "supplied", icon: Truck, title: "Versorgt durch Zentralwerkstatt", desc: "Wird durch folgende Zentralwerkstatt an bestimmten Wochentagen versorgt — deren Standort wird zusätzlich angezeigt." },
    { v: "central", icon: Network, title: "Zentralwerkstatt für andere Händler", desc: "Diese Werkstatt versorgt die folgenden Händler — deren Standorte werden zusätzlich angezeigt." },
];

export default function WorkshopLinkageBlock({ value, onChange }) {
    const mode = value.workshop_linkage_mode || "standalone";
    const supplyDays = value.workshop_supply_days || [];
    const served = value.workshop_served_dealer_ids || [];

    const toggleDay = (k) => {
        const next = supplyDays.includes(k) ? supplyDays.filter((d) => d !== k) : [...supplyDays, k];
        onChange("workshop_supply_days", next);
    };
    const addServed = (d) => {
        if (served.some((s) => (s.id || s) === d.id)) return;
        onChange("workshop_served_dealer_ids", [...served, { id: d.id, name: d.name }]);
    };
    const removeServed = (id) => onChange("workshop_served_dealer_ids", served.filter((s) => (s.id || s) !== id));

    return (
        <div className="border-t border-gray-100 pt-4" data-testid="workshop-linkage-block">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">Werkstatt-Verknüpfung</div>
            <p className="text-[11px] text-gray-500 mb-3">Legt fest, welche Werkstatt-Standorte für Miet-/Werkstatt-/Ersatzfahrzeuge zur Auswahl stehen.</p>

            <div className="space-y-2">
                {RADIO.map((r) => {
                    const Icon = r.icon;
                    const active = mode === r.v;
                    return (
                        <label
                            key={r.v}
                            className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${active ? "border-[#111827] bg-gray-50" : "border-gray-200 hover:border-gray-300"}`}
                            data-testid={`workshop-linkage-mode-${r.v}`}
                        >
                            <input
                                type="radio" name="workshop_linkage_mode" className="mt-1"
                                checked={active} onChange={() => onChange("workshop_linkage_mode", r.v)}
                                data-testid={`workshop-linkage-radio-${r.v}`}
                            />
                            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-[#111827]" : "text-gray-400"}`} />
                            <div className="min-w-0">
                                <div className="text-sm font-bold text-[#111827]">{r.title}</div>
                                <div className="text-[11px] text-gray-500">{r.desc}</div>

                                {active && r.v === "supplied" && (
                                    <div className="mt-3 space-y-2" onClick={(e) => e.preventDefault()}>
                                        {value.workshop_central_dealer_id ? (
                                            <div className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-sm px-3 py-2" data-testid="workshop-central-selected">
                                                <span className="text-sm font-medium">{value.workshop_central_dealer_name || "Zentralwerkstatt"}</span>
                                                <button type="button" onClick={() => { onChange("workshop_central_dealer_id", ""); onChange("workshop_central_dealer_name", ""); }} className="text-gray-400 hover:text-red-600" data-testid="workshop-central-remove"><X className="w-4 h-4" /></button>
                                            </div>
                                        ) : (
                                            <DealerSearch
                                                testid="workshop-central-search"
                                                placeholder="Zentralwerkstatt (Händler) suchen…"
                                                onPick={(d) => { onChange("workshop_central_dealer_id", d.id); onChange("workshop_central_dealer_name", d.name); }}
                                            />
                                        )}
                                        <div>
                                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Versorgt an Wochentagen</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {WEEKDAYS.map((d) => {
                                                    const on = supplyDays.includes(d.k);
                                                    return (
                                                        <button
                                                            key={d.k} type="button" onClick={() => toggleDay(d.k)}
                                                            className={`text-[11px] font-bold px-2.5 py-1 rounded-sm border ${on ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"}`}
                                                            data-testid={`workshop-supply-day-${d.k}`}
                                                        >{d.l}</button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {active && r.v === "central" && (
                                    <div className="mt-3 space-y-2" onClick={(e) => e.preventDefault()}>
                                        <DealerSearch
                                            testid="workshop-served-search"
                                            placeholder="Händler hinzufügen…"
                                            exclude={served.map((s) => s.id || s)}
                                            onPick={addServed}
                                        />
                                        {served.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5" data-testid="workshop-served-list">
                                                {served.map((s) => (
                                                    <span key={s.id || s} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-sm pl-2.5 pr-1.5 py-1 text-xs">
                                                        {s.name || s.id || s}
                                                        <button type="button" onClick={() => removeServed(s.id || s)} className="text-gray-400 hover:text-red-600" data-testid={`workshop-served-remove-${s.id || s}`}><X className="w-3 h-3" /></button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}
